import type { RequestConfig } from '@chikara/types';

export interface ApiRequestMeta {
  endpoint: string;
  method: string;
  requestUrl: string;
}

export interface ApiRequestErrorContext extends ApiRequestMeta {
  response: Response;
}

export interface ApiNetworkErrorContext extends ApiRequestMeta {
  error: unknown;
}

export interface ApiClientConfig {
  baseUrl: string;
  getToken?: () => string | null;
  /**
   * Allows skipping Authorization header for public endpoints (e.g. login).
   * If omitted, Authorization is attached whenever getToken() returns a token.
   */
  shouldAttachAuth?: (endpoint: string, options: ApiRequestOptions) => boolean;
  beforeAuthenticatedRequest?: () => Promise<void>;
  onUnauthorized?: () => void;
  onError?: (context: ApiRequestErrorContext) => void | Promise<void>;
  onNetworkError?: (context: ApiNetworkErrorContext) => void | Promise<void>;
  defaultHeaders?: Record<string, string>;
}

export interface ApiRequestOptions extends RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

/** A binary attachment fetched by {@link ApiClient.download}. */
export interface ApiDownloadResult {
  blob: Blob;
  /** Filename advertised by `Content-Disposition`, or null when absent. */
  filename: string | null;
}

/** Reads the filename out of a `Content-Disposition` header, or null. */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (!match) return null;
  const raw = (match[1] ?? '').trim();
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export class ApiClient {
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  private async handleResponse<T>(
    response: Response,
    requestMeta: ApiRequestMeta
  ): Promise<T> {
    if (!response.ok) {
      if (response.status === 401 && this.config.onUnauthorized) {
        this.config.onUnauthorized();
      }

      if (this.config.onError) {
        await this.config.onError({ response, ...requestMeta });
      }

      throw new Error('Unhandled API error');
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return undefined as T;
    }

    let data;
    try {
      const clone = response.clone();
      data = await clone.json();
    } catch {
      // Not JSON, continue normally with original error handling
      return response.json();
    }
    if (data && data.data && typeof data.data === 'object' && 'response' in data.data && data.data.response === 'BAD') {
      if (this.config.onError) {
        const fakeResponse = new Response(JSON.stringify(data.data), {
          status: 400,
          statusText: 'Bad Request',
          headers: response.headers
        });
        await this.config.onError({ response: fakeResponse, ...requestMeta });
      }
      throw new Error('Unhandled API error (BAD response)');
    }

    return data as T;
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean> | string): string {
    const url = new URL(`${this.config.baseUrl}${endpoint}`);

    if (params) {
      if (typeof params === "object") {
        Object.entries(params).forEach(([key, value]) => {
          url.searchParams.append(key, String(value));
        });
      } else if (typeof params === "string") {
        url.pathname += `/${params}`;
      }
    }

    return url.toString();
  }

  async request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers, params, timeout, signal } = options;
    const requestUrl = this.buildUrl(endpoint, params);
    const requestMeta: ApiRequestMeta = { endpoint, method, requestUrl };

    const shouldAttachAuth = this.config.shouldAttachAuth
      ? this.config.shouldAttachAuth(endpoint, options)
      : true;

    if (shouldAttachAuth && this.config.beforeAuthenticatedRequest) {
      await this.config.beforeAuthenticatedRequest();
    }

    const token = shouldAttachAuth ? this.config.getToken?.() : null;

    const requestHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...this.config.defaultHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    };

    const controller = new AbortController();
    const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;

    try {
      const response = await fetch(requestUrl, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal: signal || controller.signal,
      });

      return this.handleResponse<T>(response, requestMeta);
    } catch (error) {
      if (this.config.onNetworkError) {
        this.config.onNetworkError({ error, ...requestMeta });
      }
      throw error;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  /**
   * Fetches a binary attachment (e.g. an exported PDF/CSV) and returns it with
   * the filename the server sent in `Content-Disposition`. Mirrors `request`'s
   * auth handling, but the body is read as a Blob instead of JSON (so it can't
   * go through {@link handleResponse}).
   */
  async download(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiDownloadResult> {
    const { method = 'POST', body, headers, params, timeout, signal } = options;
    const requestUrl = this.buildUrl(endpoint, params);
    const requestMeta: ApiRequestMeta = { endpoint, method, requestUrl };

    const shouldAttachAuth = this.config.shouldAttachAuth
      ? this.config.shouldAttachAuth(endpoint, options)
      : true;

    if (shouldAttachAuth && this.config.beforeAuthenticatedRequest) {
      await this.config.beforeAuthenticatedRequest();
    }

    const token = shouldAttachAuth ? this.config.getToken?.() : null;

    const requestHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...this.config.defaultHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    };

    const controller = new AbortController();
    const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;

    let response: Response;
    try {
      response = await fetch(requestUrl, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal: signal || controller.signal,
      });
    } catch (error) {
      if (this.config.onNetworkError) {
        this.config.onNetworkError({ error, ...requestMeta });
      }
      throw error;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }

    if (!response.ok) {
      if (response.status === 401 && this.config.onUnauthorized) {
        this.config.onUnauthorized();
      }
      if (this.config.onError) {
        await this.config.onError({ response, ...requestMeta });
      }
      throw new Error('Unhandled API error');
    }

    return {
      blob: await response.blob(),
      filename: parseContentDispositionFilename(response.headers.get('content-disposition')),
    };
  }

  private wrapPayload(body: unknown): unknown {
    if (body === undefined || body === null) {
      return { data: {} };
    }

    if (typeof body === 'object' && !Array.isArray(body)) {
      const keys = Object.keys(body as Record<string, unknown>);
      if (keys.length === 1 && keys[0] === 'data') {
        return body;
      }
    }

    return { data: body };
  }

  // Convenience methods
  async get<T>(endpoint: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body: this.wrapPayload(body) });
  }

  async put<T>(endpoint: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body: this.wrapPayload(body) });
  }

  async patch<T>(endpoint: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body: this.wrapPayload(body) });
  }

  async delete<T>(endpoint: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Factory function for creating configured API clients
export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}
